/**
 * Показ накопленных позиций участников рынка.
 *
 * Чистый модуль без React по правилу lib/*.ts: vitest в этом проекте включает
 * только `*.test.ts`, компонентных тестов нет, поэтому всё, что решает, ЧТО за
 * число видит оператор, живёт здесь.
 *
 * # Что здесь решается
 *
 * 1. СВЕДЕНИЕ ИСХОДОВ. Бэкенд отдаёт накопление по строке на токен и намеренно
 *    не складывает половины: сопоставить токен с исходом может только справочник
 *    рынка. На бинарном рынке держать Down — это быть в шорте по Up, поэтому у
 *    кошелька одно направление, а не два. Не сведи их — и один мейкер выглядел
 *    бы двумя участниками с противоположными взглядами.
 *
 * 2. ОРИЕНТАЦИЯ ЗНАКА. Направление показывается как «+709.6 Down», а не
 *    «−709.6 Up»: читать минус как «лонг другого исхода» оператор не должен. Раз
 *    знак развёрнут, развернуть обязаны и колонки роли — иначе «нетто = тейкером
 *    плюс мейкером» на экране перестанет сходиться.
 *
 * 3. ОБРЕЗАННАЯ ВЫБОРКА ГАСИТ РОЛЬ. Мейкерская часть получается вычитанием двух
 *    выборок, поэтому у обрезанной истории она не «менее точная», а неверная:
 *    замер 2026-08-10 на рынке, упёршемся в потолок offset, дал расхождение сумм
 *    в 137 %. Показать такое число рядом с честным нельзя, и решается это здесь,
 *    а не в разметке.
 *
 * 4. ПРОЧЕРК ВМЕСТО НУЛЯ У СТАТИСТИКИ КОРОТКИХ РЫНКОВ. Отсутствие блока `flow`
 *    означает «кошелёк не обходили» — оборот ниже порога либо очередь до него не
 *    дошла, — и это НЕ «наторговал ноль». Ноль на этом месте уверенно назвал бы
 *    мелким участника, о котором мы ничего не знаем; та же причина, по которой в
 *    ADR-0007 `unknown` отделён от `noise`.
 *
 * 5. ГЛУБИНА СВОДА — ВИДИМАЯ, а не подсказкой. Окно спрашивает оператор (сегодня,
 *    3, 7, 30 суток, вся глубина), но фактических суток у кошелька может быть
 *    меньше: за один обход глубже двух суток не заглянуть. Разбор 2026-08-12: на
 *    экране стояло −$2 187 «за неделю», а это были ровно двое убыточных суток
 *    кошелька, у которого за всю глубину +$35 431. Число было верным, вывод — нет.
 */

import type {
  FlowWindow,
  ParticipantCohortKind,
  ParticipantRow,
  ParticipantToken,
  ParticipantsStats,
  Token,
  WalletFlow,
  WalletIncentives,
  WalletSettlement,
  WalletTier,
} from "../api/types";
import { formatAge, plural } from "./format";
import {
  SCALE,
  add,
  div,
  fixed,
  mul,
  parse,
  roundToFixed,
  type Micro,
} from "./money";
import { formatDollars, formatShares } from "./tape";

/**
 * Исходы рынка: то самое сопоставление токена с «Up»/«Down», которого нет в
 * ответе бэкенда и быть не должно — справочник рынка у интерфейса уже есть.
 */
type Outcomes = readonly Token[];

/**
 * Колонки, по которым таблицу можно переставить.
 *
 * Имена совпадают с полями строки, и это не экономия: сортируют ровно то, что в
 * колонке написано, а расхождение имени с полем однажды стало бы сортировкой по
 * соседнему числу.
 */
export type ParticipantSortKey =
  | "net"
  | "taker"
  | "maker"
  | "avg"
  | "trades"
  | "settled"
  | "took"
  | "tookShare"
  | "after"
  | "priceMove"
  | "bets"
  | "avgBet"
  | "pnl"
  | "roi";

/**
 * Те же величины СЫРЫМИ числами. null означает прочерк на экране — «неизвестно»,
 * а не ноль, и обходится это в sortLines отдельно.
 */
export type ParticipantSortValues = {
  [K in ParticipantSortKey]: number | null;
};

/**
 * Строка таблицы накопления.
 *
 * `taker` и `maker` могут быть null, и это не «ноль»: null означает «выборка
 * обрезана, роль не восстановима». Пустая ячейка на этом месте честна, а ноль
 * соврал бы про кошелёк, что тот не бил.
 */
export interface ParticipantLine {
  wallet: string;
  /** Имя из держателей либо пусто — тогда вызывающий покажет адрес. */
  name: string;
  ours: boolean;
  /**
   * НАША квалификация кошелька из ответа — основание, с которого таблица
   * рисуется сразу.
   *
   * До 2026-08-12 значок брался только из горячего потока `profile_update`, а тот
   * приходит при СМЕНЕ тира: свежая вкладка не получала ни одного, и «неизвестен»
   * стояло в каждой строке — при 1194 посчитанных тирах в базе. Поток никуда не
   * делся и по-прежнему дорисовывает строку, но он больше не единственный
   * источник.
   *
   * undefined означает «профиля нет» — мы с этим кошельком не торговали, а тир
   * считается по нашим встречам. Значка в таком случае нет вовсе.
   */
  tier: WalletTier | undefined;
  /**
   * Подгруппа сводки при умолчальной ступени: «тейкер+», «выплаты», «мейкер−».
   * undefined — «остальные» либо сводки нет: чипа нет, как и у тира.
   */
  profile: ParticipantCohortKind | undefined;
  /**
   * РУЧНЫЕ пометки оператора. Всегда массив, пусть и пустой: чипы рисуются
   * перебором, и ветка «поля нет» означала бы второе место, где решается, что
   * такое «не помечен».
   *
   * Рядом с tier и profile, но это ДРУГОЕ утверждение: те две выведены
   * обходами и переписываются ими, а эту произнёс человек.
   */
  tags: string[];
  /** Исход, по которому кошелёк В ЛОНГЕ. Пусто, если направления нет. */
  outcome: string;
  /** Направление в share, ВСЕГДА неотрицательное: знак несёт outcome. */
  net: string;
  taker: string | null;
  maker: string | null;
  /**
   * Средняя цена ПОКУПОК ТОКЕНА показанного направления. Пусто, если этот токен
   * кошелёк не покупал вовсе — например, набрал лонг Up, продавая Down.
   *
   * Именно по токену, а не по кошельку: средняя между Up по 0.2 и Down по 0.8
   * не значит ничего.
   */
  avg: string;
  trades: number;

  /**
   * Статистика КОРОТКИХ РЫНКОВ, а не этого окна. Все четыре поля null вместе:
   * «кошелька не обходили». Порознь они не бывают — кроме roi, у которого своя
   * причина отсутствия (кошелёк ничего не покупал).
   */
  bets: number | null;
  /** Средняя сумма ставки: оборот, делённый на число ставок. */
  avgBet: string | null;
  /** PnL со знаком, в долларах. */
  pnl: string | null;
  /**
   * Тот же PnL ДО вычета тейкерской комиссии и сама комиссия — разбор, которым
   * объясняется расхождение с профилем биржи.
   *
   * Оба поля существуют ради одного вопроса, который оператор задаёт каждый раз:
   * «почему у нас минус, а у Polymarket плюс». Замер 2026-08-12 по кошельку
   * `0x251c…541f`: наши −$7 001 против +$14 344 у биржи за те же трое суток —
   * разница ровно комиссия, $20 739 за три дня при 14 892 агрессивных сделках.
   * Биржа комиссию из прибыли не вычитает, мы вычитаем, и без разбора рядом это
   * выглядит ошибкой счёта.
   */
  pnlGross: string | null;
  fee: string | null;
  /** PnL на доллар, занесённый покупками, в процентах. */
  roi: string | null;
  /**
   * Сколько СУТОК стоит за этими числами. Ноль означает «не обходили».
   *
   * Показывается рядом с ними, потому что своду по одним суткам оператор верит
   * иначе, чем по семи: у нового кошелька глубже история не влезает в потолок
   * offset источника, и прочитать сутки как неделю — ровно та ошибка, ради
   * которой поле и заведено.
   */
  depthDays: number;
  /**
   * Та же глубина КОРОТКОЙ подписью рядом с пнл: «2д».
   *
   * Видимой, а не подсказкой при наведении, и это правка по разбору 2026-08-12:
   * на экране стояло −$2 187 под шапкой «на коротких рынках», а за числом были
   * ровно двое суток из семи запрошенных — те самые два убыточных дня кошелька,
   * у которого за всю глубину +$35 431. Подсказка эту разницу не спасала: её
   * никто не открывает, пока не заподозрит неладное.
   *
   * Пусто, когда кошелька не обходили: там и числа прочерк.
   */
  depthLabel: string;
  /**
   * В свод входят сегодняшние, ЕЩЁ НЕ ЗАКОНЧЕННЫЕ сутки.
   *
   * У пятиминутных серий дневной PnL набирается за часы, поэтому свод, взятый в
   * 03:00 UTC, описывает три часа торгов, а выглядит суточным.
   */
  partial: boolean;
  /**
   * Сколько суток окна записаны ПРОЧЕРКОМ и в pnl не вошли.
   *
   * Ноль — обычное состояние. Ненулевое означает, что деньги и роль этих суток
   * выброшены как неверные: выборки истории разошлись по глубине, и комиссия
   * там начислена на сделки, которых нет в обороте. Пнл и РОЙ рядом посчитаны
   * без этих суток, а ставки и обороты считают все.
   */
  unusableDays: number;
  /**
   * Сколько кошельку ДОПЛАТИЛИ помимо сделок за то же окно — со знаком.
   *
   * Отдельной колонкой, а не внутри пнл, и это по существу: «наторговал» и
   * «доплатили» — разные величины, а кошелёк, живущий на возврате комиссии, по
   * одному пнл выглядит убыточным. Замер 2026-08-18: программа возвращает 64 %
   * всей комиссии рынка, и с её учётом «чистые агрессоры» за неделю не −$197 тыс.,
   * а +$30.7 тыс.
   *
   * null — «выплат не видели»: не спрашивали либо переводов за окно не было.
   * Ноль здесь был бы уверенным «биржа не платит», которого прочерк не знает, —
   * тот же прочерк против нуля, что у всего блока flow.
   */
  paid: string | null;
  /**
   * Разбор выплаты словами: сколько возврата тейкеру, сколько мейкеру, сколько
   * наград и за сколько переводов. Пусто, когда выплат нет вовсе.
   *
   * Собирается здесь, а не в разметке, по правилу модуля: подсказка объясняет,
   * ЧТО за число в ячейке. Разбивка важна: возврат тейкеру и возврат мейкеру
   * описывают противоположные способы торговать.
   */
  paidTitle: string;

  /**
   * Итог кошелька по погашению ЭТОГО окна, со знаком: «+$48.25».
   *
   * Не путать с `pnl` выше: тот — свод по ВСЕМ коротким рынкам за выбранное
   * окно суток, ответ на вопрос «кто он вообще». Здесь — результат в том окне,
   * которое оператор смотрит. Числа расходятся законно и на порядки, поэтому
   * колонки стоят в разных группах таблицы.
   *
   * null означает «не знаем», а не ноль: исход не подтверждён, выборка обрезана
   * либо расписание комиссии неизвестно. Причина одна на всю таблицу и приезжает
   * отдельным полем ответа — см. settledReason в участниках панели.
   */
  settled: string | null;
  /**
   * Разбор итога словами: сколько до комиссии и сколько комиссии.
   *
   * Отвечает на вопрос, который оператор задаёт каждый раз: «почему у нас минус,
   * а у Polymarket плюс». Биржа показывает прибыль БЕЗ комиссии, мы её вычитаем.
   */
  settledTitle: string;

  // ---------------------------------------------------------------------
  // Режим «против нас». Все три поля null вне режима — их просто нет в ответе.
  // ---------------------------------------------------------------------

  /**
   * Сколько шеров кошелёк взял У НАС в этом рынке — обе роли вместе.
   *
   * Складываются здесь и только для показа: раздельные суммы едут в подсказку
   * (`tookTitle`), потому что впитывающий мейкер и агрессор требуют разного
   * поведения. В колонке нужно одно число — «сколько прошло через него от меня».
   */
  took: string | null;
  /**
   * Разбор той же величины словами: сколько он принял нашего налива, сколько снял
   * с нас и в каких деньгах. Пусто вне режима.
   *
   * Собирается здесь, а не в разметке, по правилу модуля: подсказка объясняет,
   * ЧТО за число в ячейке, то есть она часть смысла таблицы.
   */
  tookTitle: string;
  /**
   * Какая доля его оборота в этом рынке пришлась на нас, в процентах.
   *
   * Главное число режима. Кошелёк, набравший пять тысяч, из которых наша тысяча,
   * — губка: он впитывает весь рынок и защищать конкретно нашу позицию ему
   * незачем. Кошелёк, у которого весь оборот — наша тысяча, держит ровно наш
   * мешок, и вот он придёт её отыгрывать.
   *
   * Пусто при обрезанной выборке: знаменатель тогда неверен, а не неточен.
   */
  tookShare: string | null;
  /**
   * Что он набрал ПОСЛЕ последней встречи с нами, со знаком и ролью.
   *
   * Мера сопротивления. Пусто — «свод про эту встречу ещё не знает» (филл свежее
   * снимка); строка «не приходил» — знание и есть ответ: против такого можно
   * стоять до экспирации.
   */
  after: string | null;
  /** Роль добора: агрессией или стоянием. Пусто, когда добора не было. */
  afterRole: string;
  /**
   * Куда ушла цена НАШЕГО токена с момента встречи, в центах и со знаком нашей
   * позиции: минус означает «против нас».
   *
   * Величина рыночная, а не персональная: цену двигал весь рынок, а не этот
   * кошелёк. Но вопрос «надо ли переставляться» задают именно про свою позицию,
   * и рядом с колонкой добора это второй ответ на него.
   */
  priceMove: string | null;

  /**
   * Те же числа сырыми — вход сортировки, а не второй набор данных для показа.
   *
   * Отдельным полем, потому что переставить по написанному нельзя: в ячейках
   * стоят «1 204.6», «+$2 187», «12 %» — с разделителем разрядов, знаком и
   * единицей, и лексикографически «1 000» встаёт перед «999». Разбирать строку
   * обратно значило бы завести второе место, где решается величина числа, —
   * причём место, зависящее от формата показа.
   */
  sort: ParticipantSortValues;
}

/**
 * Выбранная сортировка. `undefined` у вызывающего означает «порядок выдачи», и
 * это не то же самое, что сортировка по нетто: выдача уже отобрана по |нетто|,
 * а сортировка переставляет только то, что доехало.
 */
export interface ParticipantSort {
  key: ParticipantSortKey;
  /** Убывание — умолчание для чисел: крупное сверху. */
  desc: boolean;
}

/**
 * Следующее состояние заголовка по щелчку: вниз → вверх → порядок выдачи.
 *
 * Третий щелчок ОБЯЗАН возвращать исходный порядок, а не крутить два состояния по
 * кругу. Порядок выдачи здесь содержателен: по нему бэкенд решал, кто попадёт в
 * отсечку, и вернуться к нему — единственный способ увидеть таблицу такой, какой
 * её отобрал сервер.
 */
export function nextSort(
  current: ParticipantSort | undefined,
  key: ParticipantSortKey,
): ParticipantSort | undefined {
  if (current?.key !== key) return { key, desc: true };
  if (current.desc) return { key, desc: false };
  return undefined;
}

/**
 * Перестановка строк по колонке.
 *
 * # Прочерк всегда внизу
 *
 * И при убывании, и при возрастании. Прочерк означает «неизвестно» — кошелька не
 * обходили, выборка обрезана, свод про встречу не знает, — и всплыв наверх при
 * сортировке по возрастанию он читался бы как «самый маленький». Это ровно та
 * подмена нуля неизвестностью, от которой отказывается весь модуль (пункт 4 в
 * шапке).
 *
 * # Равные значения сохраняют порядок выдачи
 *
 * Сортировка в JS устойчива по стандарту, и полагаться на это здесь можно: у
 * десятка кошельков с нулём в колонке порядок остаётся тем, который назначил
 * бэкенд, то есть по |нетто|. Иначе равные строки перемешивались бы на каждом
 * обходе.
 */
export function sortLines(
  lines: ParticipantLine[],
  sort: ParticipantSort | undefined,
): ParticipantLine[] {
  if (!sort) return lines;
  const dir = sort.desc ? -1 : 1;
  return [...lines].sort((a, b) => {
    const x = a.sort[sort.key];
    const y = b.sort[sort.key];
    if (x === null || y === null) {
      if (x === y) return 0;
      return x === null ? 1 : -1;
    }
    return (x - y) * dir;
  });
}

/**
 * Окна статистики в порядке показа. Значения — контракт с бэкендом (?flow=).
 *
 * Список ЗДЕСЬ, а не в разметке, потому что это часть смысла таблицы: подпись
 * «всё» обязана говорить не «вся история кошелька», а «вся накопленная глубина»,
 * и место, где это сказано, должно быть одно.
 */
export const FLOW_WINDOWS: readonly {
  value: FlowWindow;
  label: string;
  title: string;
}[] = [
  {
    value: "today",
    label: "сегодня",
    title: "с полуночи UTC — эти сутки ещё идут",
  },
  {
    value: "3d",
    label: "3д",
    title: "трое календарных суток UTC, считая сегодняшние",
  },
  {
    value: "7d",
    label: "7д",
    title: "семь календарных суток UTC, считая сегодняшние",
  },
  {
    value: "30d",
    label: "30д",
    title: "тридцать календарных суток UTC, считая сегодняшние",
  },
  {
    value: "all",
    label: "всё",
    title:
      "вся НАКОПЛЕННАЯ глубина, а не вся история кошелька: сутки старше срока " +
      "хранения убираются, а глубже двух суток за один обход не заглянуть",
  },
];

/** Подпись окна для шапки таблицы. Незнакомое значение отдаётся как есть. */
export function flowWindowLabel(window: FlowWindow | undefined): string {
  if (!window) return "";
  return FLOW_WINDOWS.find((w) => w.value === window)?.label ?? window;
}

/**
 * Таблица накопления, сверху вниз.
 *
 * Порядок по умолчанию задаёт бэкенд — по |нетто|, убывая, — и он же определяет,
 * КТО попал в отсечку. Поэтому перестановка оператором остаётся перестановкой
 * доехавших строк, а не выборкой по новому признаку: рынок с тысячей кошельков
 * отдаёт сотню крупнейших, и «худший пнл» среди них — не худший пнл рынка. Это
 * сказано словами в hiddenNote, а не оставлено на догадку.
 *
 * Пустой массив — данных нет; вызывающий обязан показать причину словами.
 */
export function participantLines(
  p: ParticipantsStats | undefined,
  tokens: Outcomes | undefined,
  sort?: ParticipantSort,
): ParticipantLine[] {
  if (!p?.known) return [];
  const outcomes = outcomeMap(tokens);
  const lines = p.rows.map((row) =>
    lineOf(row, outcomes, tokens, p.truncated, p.last_price ?? {}),
  );
  return sortLines(lines, sort);
}

function lineOf(
  row: ParticipantRow,
  outcomes: Map<string, string>,
  tokens: Outcomes | undefined,
  truncated: boolean,
  lastPrice: Record<string, string>,
): ParticipantLine {
  const dir = direction(row.tokens, tokens);

  // Знак разворачивается вместе с подписью исхода — и вместе с ролью, иначе
  // сумма колонок на экране перестанет сходиться с нетто.
  const flip = dir.net < 0;
  const sign = flip ? -1 : 1;
  const assetID = flip ? dir.shortAsset : dir.longAsset;

  const taker = truncated ? null : noNegZero(sign * dir.taker);
  const maker = truncated ? null : noNegZero(sign * dir.maker);
  // Средняя цена — тоже про показанный токен, поэтому её нет там, где нет
  // подписи исхода: назвать цену, не назвав, ЧЕГО она, значит предложить
  // прочитать её как цену рынка.
  const avg = dir.net === 0 ? null : avgBuyPrice(row.tokens, assetID);
  const trades = row.tokens.reduce((sum, t) => sum + t.trades, 0);
  const flow = flowCells(row.flow);
  const paid = paidCells(row.incentives);
  const met = againstCells(row, truncated, lastPrice);
  const won = settledCells(row.settled);

  const line: ParticipantLine = {
    wallet: row.wallet,
    name: row.name ?? "",
    ours: Boolean(row.ours),
    tier: row.tier,
    profile: row.profile,
    tags: row.tags ?? [],
    // Нулевое нетто — направления нет, и называть исход нечем: кошелёк не занял
    // ни одной стороны. Роль при этом остаётся видимой, и в ней весь смысл такой
    // строки: мейкер, закрывший окно в ноль, прогнал через себя оборот.
    outcome: dir.net === 0 ? "" : (outcomes.get(assetID) ?? ""),
    net: formatShares(micro(Math.abs(dir.net))),
    // Обрезанная выборка гасит роль целиком, а не подкрашивает её.
    taker: taker === null ? null : formatShares(micro(taker)),
    maker: maker === null ? null : formatShares(micro(maker)),
    avg: avg === null ? "" : roundToFixed(avg, 3),
    trades,
    ...flow.cells,
    ...paid,
    ...met.cells,
    ...won.cells,
    sort: {
      // По МОДУЛЮ нетто — ровно то число, что стоит в колонке: знак вынесен в
      // подпись исхода, и сортировка со знаком развела бы Up и Down по краям
      // таблицы, отвечая на вопрос, которого колонка не задаёт.
      net: Math.abs(dir.net),
      taker,
      maker,
      avg,
      trades,
      ...flow.sort,
      ...met.sort,
      ...won.sort,
    },
  };
  return line;
}

/**
 * Четыре колонки режима «против нас».
 *
 * Отдельной функцией по той же причине, что flowCells: здесь считаются
 * производные — доля от оборота, добор после встречи и сдвиг цены, — и все три
 * выводятся из чисел, которые уже приехали. Считать их на сервере значило бы
 * завести второе место расчёта той же величины.
 */
function againstCells(
  row: ParticipantRow,
  truncated: boolean,
  lastPrice: Record<string, string>,
): {
  cells: Pick<
    ParticipantLine,
    "took" | "tookTitle" | "tookShare" | "after" | "afterRole" | "priceMove"
  >;
  sort: Pick<
    ParticipantSortValues,
    "took" | "tookShare" | "after" | "priceMove"
  >;
} {
  const met = row.against_us;
  if (!met) {
    return {
      cells: {
        took: null,
        tookTitle: "",
        tookShare: null,
        after: null,
        afterRole: "",
        priceMove: null,
      },
      sort: { took: null, tookShare: null, after: null, priceMove: null },
    };
  }

  const taken = parseOr0(met.took) + parseOr0(met.hit);
  // Обрезанная выборка гасит долю целиком: знаменатель берётся из свода, и на
  // обрезанном рынке он занижен, то есть доля получилась бы завышенной — тем
  // сильнее, чем крупнее кошелёк. Тот же довод, что у колонок роли.
  const share = truncated ? null : takenShare(taken, row.tokens);
  const moved = truncated ? null : priceMoveOf(met, lastPrice);
  const after = afterCells(row);
  return {
    cells: {
      took: formatShares(micro(taken)),
      tookTitle: tookTitleOf(met),
      tookShare: share === null ? null : `${roundToFixed(share, 0)} %`,
      ...after.cells,
      // В центах: цены рынка живут в диапазоне [0,1], и «+0.03» на экране рядом
      // с шерами читалось бы как три сотых шера. Умножение на сто — это и есть
      // перевод микро-долей в микро-центы, никакого деления на SCALE не нужно.
      priceMove:
        moved === null
          ? null
          : `${signPrefix(moved)}${roundToFixed(Math.abs(moved) * 100, 1)}¢`,
    },
    sort: {
      took: taken,
      tookShare: share,
      after: after.sort,
      priceMove: moved,
    },
  };
}

/**
 * Разбор встреч словами.
 *
 * Две половины называются раздельно, потому что требуют РАЗНОГО поведения: тот,
 * кто принял наш налив, стоял в стакане и мог просто подставить заявку; тот, кто
 * снял нас, пришёл сам — и придёт снова.
 */
function tookTitleOf(met: NonNullable<ParticipantRow["against_us"]>): string {
  const parts: string[] = [];
  if (parseOr0(met.took) > 0) {
    parts.push(
      `принял наш налив: ${formatShares(met.took)} share на ` +
        `${formatDollars(fixed(parseOr0(met.took_usd)))} (мы били, он стоял)`,
    );
  }
  if (parseOr0(met.hit) > 0) {
    parts.push(
      `снял нас: ${formatShares(met.hit)} share на ` +
        `${formatDollars(fixed(parseOr0(met.hit_usd)))} (мы стояли, бил он)`,
    );
  }
  parts.push(`встреч ${met.trades}`);
  return parts.join("\n");
}

/** Доля наших шеров в обороте кошелька по этому рынку, в процентах. */
function takenShare(taken: number, tokens: ParticipantToken[]): Micro | null {
  // Оборот обеих сторон, а не |нетто|: мейкер, купивший и раздавший то же
  // количество, по нетто выглядел бы нулём — при том, что это ровно тот
  // участник, про которого спрашивают.
  let gross = 0;
  for (const t of tokens) {
    gross += parseOr0(t.bought) + parseOr0(t.sold);
  }
  if (gross <= 0) return null;
  // Отношение считается делением, а произведение микро-единиц — нет: taken * 100
  // * SCALE выходит за пределы точного целого в JS уже на миллионе шеров.
  // Доля больше ста процентов законна и осмысленна: наши шеры считаются по
  // НАШЕМУ размеру, а его оборот — по строкам индексатора, которые до сделки
  // могли не доехать. Зажимать её значило бы прятать отставание источника.
  return Math.round((taken / gross) * 100 * SCALE);
}

/**
 * Добор после встречи и его роль.
 *
 * Роль — главное: набранное АГРЕССИЕЙ означает «пришёл защищать позицию, цену
 * подвинет он», набранное стоянием — «просто подставил заявку». Разделять их
 * приходится, потому что одно и то же число шеров значит здесь противоположное.
 */
function afterCells(row: ParticipantRow): {
  cells: Pick<ParticipantLine, "after" | "afterRole">;
  // Ноль у «не приходил» — это ЗНАНИЕ, и в сортировке он обязан быть нулём:
  // кошелёк действительно ничего не добрал. Прочерк рядом означает другое —
  // свод про встречу ещё не знает, — и уходит вниз таблицы как неизвестность.
  sort: number | null;
} {
  const after = row.after;
  // Поля нет — обход про встречу ещё не знал. Прочерк, а не ноль.
  if (!after) return { cells: { after: null, afterRole: "" }, sort: null };
  if (after.tokens.length === 0) {
    return { cells: { after: "не приходил", afterRole: "" }, sort: 0 };
  }

  let net = 0;
  let taker = 0;
  for (const t of after.tokens) {
    net += parseOr0(t.net);
    taker += parseOr0(t.taker);
  }
  if (net === 0 && taker === 0) {
    return { cells: { after: "не приходил", afterRole: "" }, sort: 0 };
  }

  return {
    cells: {
      after: `${signPrefix(net)}${formatShares(micro(Math.abs(net)))}`,
      // Роль называется по ПРЕОБЛАДАЮЩЕЙ половине: смешанный добор бывает, и
      // выписывать оба числа в узкую ячейку значило бы не сказать ничего.
      afterRole:
        Math.abs(taker) >= Math.abs(net - taker) ? "тейкером" : "мейкером",
    },
    sort: net,
  };
}

/**
 * Сдвиг цены нашего токена с момента встречи, в центах и в НАШУ систему знаков.
 *
 * Знак разворачивается по нашей стороне: мы купили — рост в плюс, мы продали —
 * рост в минус. Иначе «+3¢» означало бы разное в соседних строках, и колонку
 * пришлось бы читать вместе с колонкой стороны, которой на экране нет.
 */
function priceMoveOf(
  met: NonNullable<ParticipantRow["against_us"]>,
  lastPrice: Record<string, string>,
): Micro | null {
  if (!met.last_asset_id || !met.last_price) return null;
  const then = parse(met.last_price);
  const now = parse(lastPrice[met.last_asset_id] ?? "");
  if (then === null || now === null) return null;

  return met.last_side === "BUY" ? now - then : then - now;
}

/**
 * Четыре колонки статистики коротких рынков.
 *
 * Отсутствие блока даёт прочерки, а не нули: см. пункт 4 в шапке модуля.
 * Отдельной функцией, потому что это единственное место, где считаются
 * производные величины — средняя ставка и РОЙ, — а хранятся они нигде: производное
 * рядом с основанием однажды с ним разойдётся.
 */
function flowCells(flow: WalletFlow | undefined): {
  cells: Pick<
    ParticipantLine,
    | "bets"
    | "avgBet"
    | "pnl"
    | "pnlGross"
    | "fee"
    | "roi"
    | "depthDays"
    | "depthLabel"
    | "partial"
    | "unusableDays"
  >;
  sort: Pick<ParticipantSortValues, "bets" | "avgBet" | "pnl" | "roi">;
} {
  if (!flow) {
    return {
      cells: {
        bets: null,
        avgBet: null,
        pnl: null,
        pnlGross: null,
        fee: null,
        roi: null,
        depthDays: 0,
        depthLabel: "",
        partial: false,
        unusableDays: 0,
      },
      sort: { bets: null, avgBet: null, pnl: null, roi: null },
    };
  }
  const avgBet = averageBet(flow);
  const pnl = parse(flow.pnl_usd);
  const roi = roiPercent(flow);
  return {
    cells: {
      bets: flow.trades,
      avgBet: avgBet === null ? null : formatDollars(fixed(avgBet)),
      pnl: signedDollars(flow.pnl_usd),
      pnlGross: grossPnL(flow),
      fee: formatFee(flow.taker_fee_usd),
      roi: formatPercent(roi),
      depthDays: flow.days,
      // Сутки, а не дата начала: у ста строк своя глубина у каждой, и колонка дат
      // читалась бы как ещё одна таблица. Ноль суток невозможен при живом flow —
      // свод без суток в базу не пишется, — но пустая подпись безопаснее «0д».
      depthLabel: flow.days > 0 ? `${flow.days}д` : "",
      partial: Boolean(flow.partial),
      unusableDays: flow.unusable_days ?? 0,
    },
    sort: { bets: flow.trades, avgBet, pnl, roi },
  };
}

/**
 * Колонка итога по погашению окна и её разбор.
 *
 * Отсутствие блока даёт прочерк, а не ноль, и различие тут дороже обычного:
 * ноль на этом месте означал бы «кошелёк вышел в ноль», а мы говорим «не
 * знаем». Причина одна на всю таблицу (исход не подтверждён, выборка обрезана
 * либо расписание комиссии неизвестно) и приезжает отдельным полем ответа —
 * повторять её в каждой строке значило бы сто раз написать одно и то же.
 */
function settledCells(settled: WalletSettlement | undefined): {
  cells: Pick<ParticipantLine, "settled" | "settledTitle">;
  sort: Pick<ParticipantSortValues, "settled">;
} {
  if (!settled) {
    return { cells: { settled: null, settledTitle: "" }, sort: { settled: null } };
  }
  const pnl = parse(settled.pnl_usd);
  const gross = parse(settled.pnl_gross_usd);
  const fee = parse(settled.fee_usd);
  const parts = [
    "итог кошелька в ЭТОМ окне по его погашению: продажи минус покупки плюс " +
      "погашение победившего токена, за вычетом тейкерской комиссии",
  ];
  if (gross !== null && fee !== null) {
    // Разбор тот же и по той же причине, что у колонки пнл: биржа показывает
    // прибыль БЕЗ комиссии, и без этих двух чисел расхождение с профилем
    // Polymarket выглядит ошибкой счёта.
    parts.push(
      `до комиссии ${signPrefix(gross)}${formatDollars(fixed(Math.abs(gross)))}, ` +
        `комиссия ${formatDollars(fixed(Math.abs(fee)))}`,
    );
  }
  return {
    cells: { settled: signedDollars(settled.pnl_usd), settledTitle: parts.join("; ") },
    sort: { settled: pnl },
  };
}

/**
 * Колонка выплат и её разбор.
 *
 * Отсутствие блока даёт прочерк, а не ноль: блока нет и когда кошелёк не
 * спрашивали, и когда переводов за окно не было, а ноль утверждал бы «биржа не
 * платит» — уверенное знание, которого за прочерком нет.
 *
 * Экспортируется ради вкладки контрагентов: колонка там та же, и вторая
 * реализация того же разбора однажды разошлась бы с этой — тот же довод, по
 * которому оттуда же берутся `returnOnInvested` и `signedDollars`.
 */
export function paidCells(
  paid: WalletIncentives | undefined,
): Pick<ParticipantLine, "paid" | "paidTitle"> {
  if (!paid) return { paid: null, paidTitle: "" };
  return {
    paid: signedDollars(paid.usd),
    paidTitle: paidTitleOf(paid),
  };
}

/** Названия типов выплат по-русски. Ключи — контракт Data API. */
const PAID_LABELS: Record<string, string> = {
  TAKER_REBATE: "возврат тейкеру",
  MAKER_REBATE: "возврат мейкеру",
  REWARD: "награды",
};

function paidTitleOf(paid: WalletIncentives): string {
  const parts: string[] = [];
  for (const [kind, label] of Object.entries(PAID_LABELS)) {
    const usd = paid.by_type?.[kind];
    if (usd === undefined) continue;
    parts.push(`${label}: ${formatDollars(fixed(parseOr0(usd)))}`);
  }
  // Незнакомый тип НЕ проглатывается: список у источника открытый, и молчаливо
  // выброшенная строка означала бы сумму, которая не сходится с разбором.
  for (const [kind, usd] of Object.entries(paid.by_type ?? {})) {
    if (kind in PAID_LABELS) continue;
    parts.push(`${kind}: ${formatDollars(fixed(parseOr0(usd)))}`);
  }
  parts.push(
    `${paid.payouts} ${plural(paid.payouts, "перевод", "перевода", "переводов")} ` +
      `за ${paid.days} ${plural(paid.days, "сутки", "суток", "суток")}`,
  );
  parts.push("начислено за предыдущие сутки: биржа платит около полуночи UTC");
  return parts.join("\n");
}

/**
 * Средняя игра на ОКНО: (покупки + продажи) / число рынков.
 *
 * # Почему знаменатель — рынки, а не сделки (изменено 2026-08-12)
 *
 * Потому что вопрос звучит «сколько он играет на одном окне», а не «каким куском
 * он это окно набирает». Дробление входа на сотню мелких филлов — обычная
 * техника исполнения, и по сделкам кошелёк, заносящий на пятиминутку $1400,
 * получал среднюю $11, то есть выглядел мельче того, кто разово поставил $50.
 *
 * Число здесь то же самое, по которому трейдер собирает реестр китов
 * (FOLLOW_MIN_AVG_USD), и разъехаться им нельзя: оператор смотрит на колонку и
 * решает, попадёт ли кошелёк в реестр.
 *
 * Оборот обеих сторон, а не только покупок: у мейкера, который купил и раздал,
 * половина работы пришлась на продажи.
 *
 * Ноль рынков даёт пустоту, а не деление на ноль: свод без сделок законен —
 * кошелёк мог появиться в наблюдении и не наторговать на коротких рынках.
 */
function averageBet(flow: WalletFlow): Micro | null {
  if (flow.markets <= 0) return null;
  const bought = parse(flow.bought_usd);
  const sold = parse(flow.sold_usd);
  if (bought === null || sold === null) return null;
  const turnover = add(bought, sold);
  if (turnover === null) return null;
  // Число рынков — обычное целое, поэтому в микро-единицы его надо ПОДНЯТЬ:
  // делить микро на целое значило бы получить результат, завышенный в миллион раз.
  return div(turnover, flow.markets * SCALE);
}

/**
 * РОЙ: PnL на доллар, ЗАНЕСЁННЫЙ ПОКУПКАМИ.
 *
 * Знаменатель — покупки, а не оборот, и это решение оператора: вопрос «сколько он
 * заработал на доллар, занесённый в рынок». По обороту то же число вышло бы вдвое
 * меньше (у закрытой позиции продажи равны покупкам) и смешивало бы вход с
 * выходом.
 *
 * Пусто, когда покупок не было вовсе: кошелёк только раздавал из стакана, и
 * «доходности на вложенное» у него нет — не ноль, а нет.
 */
export function returnOnInvested(flow: WalletFlow): string | null {
  return formatPercent(roiPercent(flow));
}

/** Он же числом — вход сортировки. Считается один раз, показывается дважды. */
function roiPercent(flow: WalletFlow): Micro | null {
  const pnl = parse(flow.pnl_usd);
  const bought = parse(flow.bought_usd);
  if (pnl === null || bought === null || bought <= 0) return null;
  const ratio = div(pnl, bought);
  if (ratio === null) return null;
  return mul(ratio, 100 * SCALE);
}

/**
 * Проценты со знаком: «+12.4 %».
 *
 * Наружу — ради строки «Итого» (lib/participanttotals.ts): РОЙ группы считается
 * из сумм, а показывается той же записью, что РОЙ строки. Второй форматтер
 * процентов рядом означал бы, что однажды они разойдутся в знаке нуля.
 */
export function formatPercent(percent: Micro | null): string | null {
  if (percent === null) return null;
  return `${signPrefix(percent)}${roundToFixed(Math.abs(percent), 1)} %`;
}

/**
 * PnL ДО вычета комиссии — то самое число, которое показывает биржа.
 *
 * Считается сложением, а не хранится: производное рядом с основанием однажды с
 * ним разойдётся (тот же довод, что у средней ставки и РОЯ). Комиссия уже
 * вычтена из pnl_usd, поэтому обратная операция — прибавление.
 */
function grossPnL(flow: WalletFlow): string | null {
  const pnl = parse(flow.pnl_usd);
  const fee = parse(flow.taker_fee_usd);
  if (pnl === null || fee === null) return null;
  const gross = add(pnl, fee);
  if (gross === null) return null;
  return `${signPrefix(gross)}${formatDollars(fixed(Math.abs(gross)))}`;
}

/** Комиссия без знака: она всегда расход, и минус перед ней был бы вторым. */
function formatFee(raw: string): string | null {
  const fee = parse(raw);
  if (fee === null) return null;
  return formatDollars(fixed(Math.abs(fee)));
}

/**
 * Деньги со ЗНАКОМ: «+$218.8», «−$31.5».
 *
 * Плюс проставляется явно, потому что колонка знаковая: без него «218.8» и
 * «−31.5» стоят рядом, и положительное число читается как «просто сумма».
 */
export function signedDollars(value: string): string | null {
  const m = parse(value);
  if (m === null) return null;
  return `${signPrefix(m)}${formatDollars(fixed(Math.abs(m)))}`;
}

/**
 * Цена со ЗНАКОМ: «+0.195», «−0.195».
 *
 * Отдельно от `signedDollars`, а не флагом в ней: там доллары с разрядами и
 * значком валюты, здесь — доля от единицы с фиксированным числом знаков, и
 * общего у них ровно один `signPrefix`. Ради него они и стоят рядом: минус
 * здесь ТИПОГРАФСКИЙ, а по нему красит `pnlColor` в разметке — разойдись они, и
 * отклонение перестало бы краснеть.
 */
export function signedPrice(value: string, digits: number): string | null {
  const m = parse(value);
  if (m === null) return null;
  return `${signPrefix(m)}${roundToFixed(Math.abs(m), digits)}`;
}

/** Знак впереди величины. Ноль остаётся без знака: он не «плюс ноль». */
function signPrefix(m: Micro): string {
  if (m > 0) return "+";
  // Минус — типографский, как в остальных подписях интерфейса.
  if (m < 0) return "−";
  return "";
}

/**
 * Направление кошелька в единицах ПЕРВОГО исхода.
 *
 * Наружу — ради строки «Итого» (lib/participanttotals.ts), которая складывает то
 * же самое по группе кошельков. Второе место, где решается «токен второго исхода
 * входит с минусом», означало бы, что сумма по группе однажды перестанет
 * сходиться с суммой её строк.
 *
 * У бинарного рынка это net(исход 0) − net(исход 1). У рынка с другим числом
 * исходов сводить нечего, и тогда берётся сумма как есть: притворяться, что
 * трёхисходный neg-risk сводится к одной оси, значило бы выдумать направление.
 */
export function direction(
  rowTokens: ParticipantToken[],
  tokens: Outcomes | undefined,
): {
  net: number;
  taker: number;
  maker: number;
  longAsset: string;
  shortAsset: string;
} {
  const first = tokens?.[0]?.asset_id ?? "";
  const second = tokens?.[1]?.asset_id ?? "";
  const binary = tokens?.length === 2 && first !== "" && second !== "";

  let net = 0;
  let taker = 0;
  let maker = 0;
  for (const t of rowTokens) {
    // Токен второго исхода входит с минусом: лонг Down — это шорт Up.
    const k = binary && t.asset_id === second ? -1 : 1;
    net += k * parseOr0(t.net);
    taker += k * parseOr0(t.taker);
    maker += k * parseOr0(t.maker);
  }

  // Какой токен показывать подписью при положительном и при отрицательном нетто.
  //
  // На НЕбинарном рынке — никакой, и это не упрощение. Подпись исхода есть
  // утверждение «кошелёк в лонге по этому исходу», а число там сложено по всем
  // исходам сразу. Поставь сюда первый токен — и таблица назвала бы сторону,
  // которой у кошелька нет. Пустая подпись честна: направления мы не знаем.
  //
  // Тот же случай — рынок без загруженного справочника токенов: имён исходов нет,
  // значит и утверждать нечего.
  return {
    net,
    taker,
    maker,
    longAsset: binary ? first : "",
    shortAsset: binary ? second : "",
  };
}

/**
 * Средняя цена покупок одного токена: bought_usd / bought.
 *
 * Считается делением точной арифметикой, а не в компоненте: `Number(a)/Number(b)`
 * — это тот самый float, которого в проекте нет нигде.
 */
function avgBuyPrice(
  rowTokens: ParticipantToken[],
  assetID: string,
): Micro | null {
  const t = rowTokens.find((x) => x.asset_id === assetID);
  if (!t) return null;
  const usd = parse(t.bought_usd);
  const shares = parse(t.bought);
  if (usd === null || shares === null || shares <= 0) return null;
  return div(usd, shares);
}

/**
 * Возраст данных: сколько прошло с самой свежей ПРОИНДЕКСИРОВАННОЙ сделки.
 *
 * Не возраст нашего запроса. Индексатор отстаёт минутами и рывками, поэтому
 * свежий ответ web_api может содержать четырёхминутную картину, и различать это
 * оператор обязан по этому числу.
 *
 * NaN — метки нет (сделок не было).
 */
export function dataAge(
  p: ParticipantsStats | undefined,
  nowMs: number,
): number {
  if (!p?.newest) return NaN;
  const at = new Date(p.newest).getTime();
  return Number.isNaN(at) ? NaN : nowMs - at;
}

/**
 * С какого возраста данные перестают описывать идущее окно.
 *
 * Планок ДВЕ, потому что источников два и норма у них разная. Индексатор
 * отстаёт минутами по устройству, и минута там — обычное дело. Живая лента
 * отдаёт сделку за секунду, и та же минута на ней означает, что трейдер молчит.
 * Одна планка на оба источника либо кричала бы на исправном индексаторе, либо
 * молчала бы на мёртвой ленте.
 */
const AGE_ALARM_MS = 60_000;
const AGE_ALARM_FEED_MS = 20_000;

/**
 * Подпись возраста данных и нужно ли её выделять.
 *
 * Возвращается ВСЕГДА, когда метка есть: на пятиминутном окне четырёхминутное
 * отставание означает, что таблица описывает прошедшее окно, а не идущее, и
 * прочитать её как свежую — ровно та ошибка, ради которой поле и заведено.
 *
 * Источник называется словом рядом с числом. «Данным 4 мин» само по себе не
 * отвечает ни на что: на индексаторе это норма, на ленте — отказ.
 */
export function ageLabel(
  p: ParticipantsStats | undefined,
  nowMs: number,
): { text: string; alarm: boolean } {
  const age = dataAge(p, nowMs);
  if (!Number.isFinite(age)) return { text: "", alarm: false };
  const feed = p?.source === "feed";
  const limit = feed ? AGE_ALARM_FEED_MS : AGE_ALARM_MS;
  const source = feed ? "лента" : "индексатор";
  return { text: `${source}, ${formatAge(age)}`, alarm: age > limit };
}

/**
 * POLL_LATE_MS — насколько опаздывать к сроку годности снимка.
 *
 * Опаздывать, а НЕ приходить вовремя, и это главное свойство расчёта. Придя на
 * миллисекунду раньше срока, опрос получил бы тот же самый снимок и уехал бы
 * ждать целый следующий такт — то есть ровно ту пилу, ради устранения которой
 * сервер и присылает такт. Опоздание стоит этих же миллисекунд и не стоит ничего
 * больше.
 *
 * POLL_MIN_MS — предохранитель от частоты: обход может тянуться дольше своего
 * срока годности, и тогда сервер честно пришлёт ноль. Две секунды — не
 * «достаточно свежо», а «не чаще этого»: за эндпоинтом чужой лимитер, а на
 * ленте срок свода 100 мс, и с полом в секунду опрос сам по себе давал до
 * шестидесяти запросов в минуту при живом стуке рядом (замер 2026-09-06:
 * 50–150 запросов в минуту на вкладку, 12–15 % единственного ядра прода).
 * Свежесть при этом даёт стук, а не опрос: опрос — страховка на потерянный
 * стук и трейдер прежней версии.
 *
 * POLL_FALLBACK_MS — когда такта нет вовсе: ответ старого сервера. Нынешние
 * пятнадцать секунд, потому что неизвестный такт чужого сервера частить нельзя.
 */
const POLL_LATE_MS = 400;
const POLL_MIN_MS = 2_000;
const POLL_FALLBACK_MS = 15_000;

/**
 * FLOW_REFRESH_MS — выдержка между стуком трейдера и перечитыванием панели.
 *
 * Стуки идут тактом раздачи трейдера (100 мс на окно), и каждый стоил бы
 * запроса; выдержка склеивает стуки одного рынка в один запрос. Стояли сто
 * миллисекунд — «тот же такт, короче не даст ничего», — и в горячую минуту
 * окна это давало до десяти запросов в секунду по 177 КБ тела каждый. Секунда
 * — та же выдержка, что у ряда (TIMELINE_REFRESH_MS), и по тому же доводу:
 * число на экране, приехавшее на полсекунды позже, глазом неотличимо, а
 * сервер за этот срок отдал бы девять одинаковых тел.
 */
export const FLOW_REFRESH_MS = 1000;

/**
 * Состояние окна трейдера: эпоха и порядковый номер (bus.MarketFlow). Едет и в
 * стуке md.flow.<condition_id>, и в теле ответа панели и ряда.
 */
export interface FlowVersion {
  epoch: number;
  seq: number;
}

function flowVersionOf(v: Partial<FlowVersion> | undefined): FlowVersion | undefined {
  if (!v) return undefined;
  const { epoch, seq } = v;
  if (typeof epoch !== "number" || typeof seq !== "number") return undefined;
  if (!Number.isFinite(epoch) || !Number.isFinite(seq)) return undefined;
  return { epoch, seq };
}

/**
 * Есть ли в стуке что-то новее уже полученных ответов.
 *
 * Стук приходит на каждый такт раздачи трейдера, а ответ, пришедший по
 * предыдущему стуку, уже несёт состояние окна на момент сборки — часто то же
 * самое или новее. Перечитывать по такому стуку — значит спросить сервер и
 * получить назад то, что на экране.
 *
 * Отсев ОСТОРОЖНЫЙ: перечитываем, если хоть чего-то не знаем. Стук без
 * номера — трейдер прежней версии; ответ без номера — индексатор Data API
 * или старый сервер; чужая эпоха — трейдер перезапущен, и сравнивать номера
 * не с чем. Ответов нет вовсе — инвалидация ничего не стоит. Хотя бы один
 * ответ отстаёт — перечитываем: ключи под одним рынком (окна статистики,
 * фильтры) инвалидируются вместе.
 */
export function flowNoticeIsNews(
  notice: Partial<FlowVersion> | undefined,
  seen: ReadonlyArray<Partial<FlowVersion> | undefined>,
): boolean {
  const n = flowVersionOf(notice);
  if (!n) return true;
  if (seen.length === 0) return true;
  return seen.some((s) => {
    const v = flowVersionOf(s);
    if (!v) return true;
    return v.epoch !== n.epoch || v.seq < n.seq;
  });
}

/**
 * Перечитывание по стуку со склейкой и отсевом уже известного.
 *
 * Стук сначала сверяется с тем, что уже в кеше запросов (ранний отсев: таймер
 * не взводится вовсе), затем склеивается по рынку на delayMs, и перед самым
 * перечитыванием сверяется ещё раз: за выдержку мог приехать ответ по
 * прежнему стуку, и он уже несёт это состояние окна. Из нескольких стуков за
 * выдержку помнится последний — номера у них растут.
 *
 * Отдельной функцией, а не внутри хука, по тому же доводу, что coalesceByKey:
 * тесты есть у lib, а таймеры и отсев ломаются молча.
 */
export function flowRefresher(
  seen: (conditionID: string) => ReadonlyArray<Partial<FlowVersion> | undefined>,
  invalidate: (conditionID: string) => void,
  delayMs: number,
  timers?: { set: (cb: () => void, ms: number) => number; clear: (id: number) => void },
): {
  fire: (conditionID: string, notice?: Partial<FlowVersion>) => void;
  dispose: () => void;
} {
  const latest = new Map<string, Partial<FlowVersion> | undefined>();
  const coalesced = coalesceByKey<string>(
    (conditionID) => {
      const notice = latest.get(conditionID);
      latest.delete(conditionID);
      if (!flowNoticeIsNews(notice, seen(conditionID))) return;
      invalidate(conditionID);
    },
    delayMs,
    timers,
  );
  return {
    fire(conditionID, notice) {
      if (!flowNoticeIsNews(notice, seen(conditionID))) return;
      latest.set(conditionID, notice);
      coalesced.fire(conditionID);
    },
    dispose() {
      latest.clear();
      coalesced.dispose();
    },
  };
}

/**
 * Склейка стуков по ключу: первый стук взводит таймер, остальные до его
 * срабатывания ничего не делают, срабатывание зовёт fn один раз.
 *
 * Отдельной функцией, а не внутри хука, потому что тесты есть у lib, а не у
 * хуков, а таймеры — это ровно то, что ломается молча.
 */
export function coalesceByKey<K>(
  fn: (key: K) => void,
  delayMs: number,
  timers: { set: (cb: () => void, ms: number) => number; clear: (id: number) => void } = {
    set: (cb, ms) => window.setTimeout(cb, ms),
    clear: (id) => window.clearTimeout(id),
  },
): { fire: (key: K) => void; dispose: () => void } {
  const pending = new Map<K, number>();
  return {
    fire(key) {
      if (pending.has(key)) return;
      pending.set(
        key,
        timers.set(() => {
          pending.delete(key);
          fn(key);
        }, delayMs),
      );
    },
    dispose() {
      for (const id of pending.values()) timers.clear(id);
      pending.clear();
    },
  };
}

/**
 * Когда спрашивать накопление снова.
 *
 * Интервал задаёт СЕРВЕР — он один знает, сколько стоил обход и сколько снимку
 * осталось жить. Своя константа на этой стороне была бы вторым таймером с
 * независимой фазой, а сумма двух независимых тактов и давала оператору до
 * тридцати секунд возраста при живом источнике (замер 2026-08-11).
 */
export function nextPollMs(nextInMs: number | undefined): number {
  if (typeof nextInMs !== "number" || !Number.isFinite(nextInMs)) {
    return POLL_FALLBACK_MS;
  }
  return Math.max(nextInMs + POLL_LATE_MS, POLL_MIN_MS);
}

/**
 * Предупреждения к таблице, словами и по-русски.
 *
 * Список, а не одна строка: причины независимы и могут прийти вместе. Пустое
 * место вместо предупреждения читалось бы как «всё в порядке», а это другой факт.
 */
export function participantWarnings(
  p: ParticipantsStats | undefined,
): string[] {
  if (!p) return [];
  const out: string[] = [];
  if (!p.known) {
    out.push(p.reason || "накопление недоступно");
    return out;
  }
  if (p.truncated) {
    out.push(
      "история рынка глубже, чем отдаёт источник: колонки «тейкером» и «мейкером» скрыты — " +
        "роль считается вычитанием выборок, и на обрезанной истории она неверна, а не приблизительна",
    );
  }
  if (p.partial) {
    // Про ПАНЕЛЬ, а не про систему. Раньше здесь стояло «сделки до подключения
    // не видны и не будут» — с 2026-09-05 это неправда: сырьё пишет отдельный
    // сервис (ADR-0028), и сделки до подключения трейдера в базе есть. Панель
    // по-прежнему живёт на его памяти, поэтому предупреждение остаётся — но
    // говорит ровно то, что верно: этих строк нет ЗДЕСЬ.
    out.push(
      "окно наблюдается не с начала: трейдер подключился, когда оно уже шло, " +
        "и в панели этих сделок нет — она показывает то, что он видел сам",
    );
  }
  if (!p.balanced && !p.partial) {
    // На окне, взятом не с начала, стороны не сходятся ЗАКОННО: часть
    // мейкерских строк пришла до подключения, а тейкерская после. Показать тут
    // «источник изменил поведение» значило бы объявить поломкой собственный
    // запуск, поэтому предупреждение выше отвечает вместо этого.
    out.push(
      "стороны сделок не сошлись: источник изменил поведение, и разбивка по роли может быть неверна",
    );
  }
  if (p.stale) {
    out.push("обновить не удалось — числа из последнего удачного снимка");
  }
  if (p.holders_reason) {
    out.push(p.holders_reason);
  }
  if (p.against) {
    out.push(...againstWarnings(p));
  }
  return out;
}

/**
 * Предупреждения режима «против нас».
 *
 * Оба про НЕВИДИМЫЕ ВСТРЕЧИ, и обоих не видно из таблицы — она показывает то, что
 * есть, и молчит о том, чего в ней нет:
 *
 *   - агрессор, ещё не восстановленный из цепочки (ADR-0008). Кошелька у такой
 *     сделки нет вовсе, и строки для неё быть не может. При выключенном
 *     восстановлении это ВСЕ наши мейкерские сделки, то есть половина свода
 *     отсутствует молча;
 *   - контрагент, известный журналу, но не найденный в своде рынка. Значит,
 *     индексатор до этой сделки не дошёл либо выборка обрезана потолком.
 */
function againstWarnings(p: ParticipantsStats): string[] {
  const out: string[] = [];
  const pending = p.against_pending ?? 0;
  if (pending > 0) {
    out.push(
      `${pending} ${plural(pending, "наша сделка", "наших сделки", "наших сделок")} ` +
        `в этом рынке ${plural(pending, "ждёт", "ждут", "ждут")} восстановления агрессора: ` +
        "мы стояли в стакане, и адрес того, кто нас снял, приходит из цепочки позже. " +
        "В таблице таких встреч НЕТ",
    );
  }
  const known = p.against_wallets ?? 0;
  const missing = known - p.wallets;
  if (missing > 0) {
    out.push(
      `${missing} ${plural(missing, "кошелёк", "кошелька", "кошельков")} из журнала ` +
        `${plural(missing, "не найден", "не найдены", "не найдены")} в своде рынка: ` +
        "индексатор до этих сделок ещё не дошёл либо выборка обрезана потолком источника",
    );
  }
  return out;
}

/** Сколько кошельков не поместилось в таблицу. 0 — поместились все. */
export function hiddenWallets(p: ParticipantsStats | undefined): number {
  if (!p?.known) return 0;
  return Math.max(0, p.wallets - p.rows.length);
}

/**
 * Подпись панели: сколько кошельков отторговало и сколько прошло сделок.
 *
 * Со склонением, потому что интерфейс русский, а «121 кошельков» читается как
 * опечатка — то есть заставляет усомниться в самом числе. Пусто, когда данных
 * нет: подпись «0 кошельков» на месте неизвестности соврала бы.
 */
export function participantsSummary(p: ParticipantsStats | undefined): string {
  if (!p?.known) return "";
  const wallets = `${p.wallets} ${plural(p.wallets, "кошелёк", "кошелька", "кошельков")}`;
  // В режиме «против нас» второе число было бы не о том: сделок по рынку тысячи,
  // а строк в таблице единицы, и рядом они читаются как обрезанная выдача. Там
  // считаются ВСТРЕЧИ — наши сделки с этими кошельками.
  if (p.against) {
    const met = p.rows.reduce((sum, r) => sum + (r.against_us?.trades ?? 0), 0);
    return `${wallets} · ${met} ${plural(met, "встреча", "встречи", "встреч")} с нами`;
  }
  const trades = `${p.trades} ${plural(p.trades, "сделка", "сделки", "сделок")}`;
  return `${wallets} · ${trades}`;
}

/**
 * Можно ли оставить прошлый свод на экране, пока едет новый.
 *
 * # Зачем вопрос вообще стоит
 *
 * Прошлый свод держится намеренно: переключение окна статистики или режима не
 * должно мигать пустой таблицей — строки «в этом окне» от них не зависят вовсе.
 * Но у той же настройки есть второе действие, которого не хотели: при переходе
 * на ДРУГОЙ РЫНОК она оставляет на экране кошельки предыдущего окна под новым
 * адресом.
 *
 * И это хуже, чем «данные на полсекунды устарели». Направление, роль и средняя
 * цена считаются здесь ПО ТОКЕНАМ рынка, а токены приезжают из справочника уже
 * нового окна: на экране оказывается гибрид двух рынков. Замер 2026-08-26,
 * 150 мс после перехода: кошелёк с нетто 3 005 показан с нетто 2 455 и пустой
 * средней ценой, а колонка «Итог» — с погашением ЧУЖОГО окна.
 *
 * Поэтому прошлый свод держится только внутри ОДНОГО рынка. Смена рынка честно
 * показывает загрузку: пустая таблица на полсекунды дешевле правдоподобно
 * неверной.
 */
export function keepsPreviousSnapshot(
  prevKey: readonly unknown[] | undefined,
  conditionID: string | undefined,
): boolean {
  if (!prevKey) return false;
  // Рынок стоит вторым в ключе (см. participantsKey). Пустая строка — то же
  // «рынка нет», что и undefined: ключ нормализует его именно так.
  return prevKey[1] === (conditionID ?? "");
}

/**
 * Подпись над колонкой «Итог»: чем окно погашено либо почему итога нет.
 *
 * # Почему причина ВИДИМАЯ, а не в подсказке
 *
 * Потому что без неё колонка — это сто прочерков подряд, и читается она как
 * «не посчитали» или «не запросили». Именно так её и прочитали при первом
 * показе. На самом деле прочерк означает одно из трёх и каждое требует своего:
 * исход ещё не подтверждён (ждать минуту), выборка обрезана (числа не будет
 * вовсе), расписание комиссии неизвестно (чинить сбор). Подсказку при наведении
 * в такой ситуации никто не открывает — её открывают, уже заподозрив неладное.
 *
 * Короткая форма — часть причины ДО двоеточия: сервер строит их одинаково
 * («что» до двоеточия, «почему» после), и правило переживёт новые причины.
 * Полный текст уезжает в подсказку.
 */
export function settlementNote(p: ParticipantsStats | undefined): {
  text: string;
  title: string;
} {
  if (!p?.known) return { text: "", title: "" };

  const winner = p.resolution?.resolved ? (p.resolution.winner ?? "").trim() : "";
  if (winner !== "") {
    return {
      text: `погашение: ${winner}`,
      title:
        `окно погашено в пользу «${winner}», и колонка «Итог» посчитана по нему: ` +
        "продажи минус покупки плюс погашение победившего токена, за вычетом " +
        "тейкерской комиссии",
    };
  }

  const reason = (p.settled_reason ?? "").trim();
  if (reason === "") return { text: "", title: "" };
  const head = reason.split(":")[0]?.trim() ?? reason;
  return { text: `итога нет: ${head}`, title: reason };
}

/**
 * Приписка об отсечке списка. Пусто, когда поместились все.
 *
 * Отсечка называется вслух: без этого сотня строк выглядела бы полным составом
 * рынка, а на живом пятиминутном окне их бывает под тысячу.
 *
 * # Что в строке, а что в подсказке
 *
 * В строке — ЗНАМЕНАТЕЛЬ: «показаны 100 крупнейших из 731». Это и есть то, ради
 * чего приписка существует, и прочитаться она обязана без наведения. Разбор
 * («ещё 631 не поместился», и чем сортировка отличается от отбора) уехал в
 * подсказку: приписка стоит под таблицей всегда, а место под ней — это строки
 * кошельков, которых иначе видно на две меньше.
 *
 * # Почему сортировка добавляет фразу
 *
 * Потому что вместе с отсечкой она обманывает. Отбор в сотню шёл по |нетто|, и
 * переставленная по пнл таблица показывает худший пнл СРЕДИ КРУПНЕЙШИХ, а
 * читается как худший пнл рынка. Пока порядок совпадает с отбором, вопроса не
 * возникает; стоит их развести — оговорка обязана быть.
 */
export function hiddenNote(
  p: ParticipantsStats | undefined,
  sorted = false,
): { text: string; title: string } {
  const hidden = hiddenWallets(p);
  if (hidden === 0 || !p?.known) return { text: "", title: "" };
  const noun = plural(hidden, "кошелёк", "кошелька", "кошельков");
  const verb = plural(
    hidden,
    "не поместился",
    "не поместились",
    "не поместились",
  );
  // Знаменатель — В САМОЙ строке, а не в подсказке: «100 из 731» и есть та
  // фраза, ради которой приписка существует, и читаться она обязана без
  // наведения.
  const text = `показаны ${p.rows.length} крупнейших по накоплению из ${p.wallets}`;
  const title = `ещё ${hidden} ${noun} ${verb}: выдача обрезана крупнейшими по накоплению${
    sorted
      ? ". Сортировка переставляет только показанные строки — в список они отобраны по накоплению"
      : ""
  }`;
  return { text, title };
}

/**
 * Что написано на месте пустой таблицы.
 *
 * # Почему текстов два
 *
 * Потому что пустота означает РАЗНОЕ, и разница выяснилась на живом рынке: в
 * режиме «против нас» на экране стояло «по этому рынку сделок пока не было» —
 * при 681 сделке и 502 кошельках в том же своде. Пустой была не история рынка, а
 * наш журнал: мы здесь ни с кем не торговали. Прочитать первое как второе значит
 * решить, что рынок мёртв, глядя на самый живой момент окна.
 *
 * Пустая строка означает «текста нет»: свод ещё не приехал, и вызывающий покажет
 * загрузку, а не утверждение о рынке, которого не видел.
 */
export function emptyNote(p: ParticipantsStats | undefined): string {
  if (!p?.known) return "";
  if (p.against) {
    return (
      "В этом рынке мы ещё ни с кем не торговали. Строка появится после первой " +
      "нашей сделки — вместе с разбором: сколько кошелёк взял у нас и что он " +
      "сделал после встречи."
    );
  }
  return "По этому рынку сделок пока не было.";
}

/**
 * Приписка к четырём колонкам справа: за что они посчитаны и чем отличаются от
 * того же числа на профиле биржи.
 *
 * # Что в строке, а что в подсказке
 *
 * В строке — оба вывода в шесть слов: за какое окно посчитано и что комиссия уже
 * вычтена. Разбор обоих (фактическая глубина по кошельку короче окна; на бирже
 * то же число будет больше) — в подсказке. Полный текст занимал под таблицей три
 * строки, то есть три строки кошельков, а читают его один раз за смену.
 *
 * # Зачем это на экране вообще, а не только в подсказке
 *
 * Обе строки написаны по разбору 2026-08-12, где панель показывала −$2 187, а
 * биржа по тому же кошельку +$2 230, и выглядело это поломкой счёта. Слагаемых
 * оказалось два, и оба невидимы:
 *
 *  1. ОКНО. Запрошенная неделя попала в двое убыточных суток (глубже свода тогда
 *     не было), тогда как за всю глубину у кошелька +$35 431.
 *  2. КОМИССИЯ. Наш пнл считается за её вычетом, профиль Polymarket — нет, и
 *     разница ровно на неё: +702.20 против +2230.34 при комиссии 1526.98 за те
 *     же сутки. Сверка сошлась до двух долларов на сорока тысячах.
 *
 * Первое лечится переключателем и подписью глубины в строке, второе — этой
 * фразой: сравнивать с биржей оператор всё равно будет.
 */
export function flowNote(
  p: ParticipantsStats | undefined,
): { text: string; title: string } {
  if (!p?.known) return { text: "", title: "" };
  const window = flowWindowLabel(p.flow_window);
  const scope = window
    ? `«на коротких рынках» — за ${window}`
    : "«на коротких рынках»";
  return {
    text: `${scope}, пнл без комиссии`,
    title:
      `${scope}. Рядом с пнл стоит фактическая глубина свода по этому кошельку, ` +
      "она бывает короче окна. Пнл — за вычетом тейкерской комиссии: на профиле " +
      "Polymarket то же число будет БОЛЬШЕ ровно на неё.",
  };
}

/**
 * Сопоставление токена с исходом — ЕДИНСТВЕННОЕ место, где оно живёт.
 *
 * Экспортируется ради сводки по группам (lib/cohorts.ts): у неё те же строки по
 * токенам и тот же справочник, а вторая копия этих пяти строк стала бы вторым
 * местом, где решается, как называется Up.
 */
export function outcomeMap(tokens: Outcomes | undefined): Map<string, string> {
  const m = new Map<string, string>();
  for (const t of tokens ?? []) m.set(t.asset_id, t.outcome);
  return m;
}

/** Микро-единицы обратно в строку — вход форматтеров lib/tape.ts. */
function micro(v: number): string {
  const neg = v < 0;
  const abs = Math.abs(Math.round(v));
  const whole = Math.trunc(abs / 1_000_000);
  const frac = String(abs % 1_000_000).padStart(6, "0");
  return `${neg ? "-" : ""}${whole}.${frac}`;
}

function parseOr0(s: string | null | undefined): number {
  return parse(s) ?? 0;
}

/**
 * Минус ноль обратно в ноль.
 *
 * Он законный итог разворота знака (−1 × 0) и сравнениям не мешает, но это
 * значение уезжает в числа сортировки, а там оно неотличимо от нуля лишь
 * оператором `-`: `Object.is(-0, 0)` уже ложь. Разного нуля в одном столбце быть
 * не должно.
 */
function noNegZero(v: number): number {
  return v === 0 ? 0 : v;
}
